import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, InputLabel, ListItemText, MenuItem,
  Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { api, endpoints } from '../config/api';
import { Tour } from '../types';

interface AppInstallation {
  _id: string;
  installationId: string;
  deviceName: string;
  location: string;
  tourAccessMode: 'all' | 'selected';
  allowedTourIds: Array<{ _id?: string; id?: string; name?: string } | string | null>;
  lastSeen: string;
}

const AppInstallations: React.FC = () => {
  const [devices, setDevices] = useState<AppInstallation[]>([]);
  const [tours, setTours] = useState<Tour[]>([]);
  const [selected, setSelected] = useState<AppInstallation | null>(null);
  const [selectedInstallationIds, setSelectedInstallationIds] = useState<string[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkNamePrefix, setBulkNamePrefix] = useState('');
  const [bulkLocation, setBulkLocation] = useState('');
  const [bulkAccessMode, setBulkAccessMode] = useState<'all' | 'selected'>('all');
  const [bulkTourIds, setBulkTourIds] = useState<string[]>([]);
  const [deviceName, setDeviceName] = useState('');
  const [location, setLocation] = useState('');
  const [accessMode, setAccessMode] = useState<'all' | 'selected'>('all');
  const [tourIds, setTourIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const [deviceResponse, tourResponse] = await Promise.all([
        api.get(endpoints.appInstallations),
        api.get(endpoints.tours),
      ]);
      setDevices(deviceResponse.data.data || []);
      setTours(tourResponse.data.data || []);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Impossibile caricare i telefoni installati.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const edit = (device: AppInstallation) => {
    setSelected(device);
    setDeviceName(device.deviceName || 'Dispositivo Android');
    setLocation(device.location || '');
    setAccessMode(device.tourAccessMode || 'all');
    setTourIds((device.allowedTourIds || [])
      .map(value => {
        if (typeof value === 'string') return value;
        return value?.id || value?._id || '';
      })
      .filter((id): id is string => Boolean(id)));
  };

  const save = async () => {
    if (!selected) return;
    try {
      await api.put(endpoints.appInstallation(selected.installationId), {
        deviceName: deviceName.trim(),
        location,
        tourAccessMode: accessMode,
        allowedTourIds: accessMode === 'selected' ? Array.from(new Set(tourIds.filter(Boolean))) : [],
      });
      setSelected(null);
      await load();
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.message || 'Impossibile salvare l’assegnazione dei tour.');
    }
  };

  const remove = async (device: AppInstallation) => {
    const confirmed = window.confirm(
      `Eliminare il dispositivo "${device.deviceName}"? Se l'app verrà riaperta su questo telefono, il dispositivo si registrerà nuovamente.`
    );
    if (!confirmed) return;

    try {
      await api.delete(endpoints.appInstallation(device.installationId));
      setDevices(current => current.filter(item => item.installationId !== device.installationId));
      setError('');
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.message || 'Impossibile eliminare il dispositivo.');
    }
  };

  const allSelected = devices.length > 0 && devices.every(device => selectedInstallationIds.includes(device.installationId));
  const someSelected = selectedInstallationIds.length > 0 && !allSelected;

  const toggleAll = () => {
    setSelectedInstallationIds(allSelected ? [] : devices.map(device => device.installationId));
  };

  const toggleDevice = (installationId: string) => {
    setSelectedInstallationIds(current => current.includes(installationId)
      ? current.filter(id => id !== installationId)
      : current.concat(installationId));
  };

  const openBulkEdit = () => {
    setBulkNamePrefix('');
    setBulkLocation('');
    setBulkAccessMode('all');
    setBulkTourIds([]);
    setBulkOpen(true);
  };

  const saveBulk = async () => {
    const orderedIds = devices
      .filter(device => selectedInstallationIds.includes(device.installationId))
      .map(device => device.installationId);
    if (orderedIds.length === 0) return;

    try {
      await api.put(endpoints.appInstallationsBulk, {
        installationIds: orderedIds,
        namePrefix: bulkNamePrefix.trim(),
        location: bulkLocation,
        tourAccessMode: bulkAccessMode,
        allowedTourIds: bulkAccessMode === 'selected'
          ? Array.from(new Set(bulkTourIds.filter(Boolean)))
          : [],
      });
      setBulkOpen(false);
      setSelectedInstallationIds([]);
      await load();
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.message || 'Impossibile aggiornare i dispositivi selezionati.');
    }
  };

  if (loading && devices.length === 0) {
    return <Box display="flex" justifyContent="center" minHeight="50vh" alignItems="center"><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Telefoni app</Typography>
      <Typography color="text.secondary" paragraph>
        I telefoni compaiono automaticamente dopo il primo caricamento dei tour. Assegna una location e limita i tour visibili sul dispositivo.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {selectedInstallationIds.length > 0 && (
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Chip label={`${selectedInstallationIds.length} selezionati`} color="primary" />
          <Button variant="contained" onClick={openBulkEdit}>Configura selezionati</Button>
          <Button onClick={() => setSelectedInstallationIds([])}>Deseleziona tutti</Button>
        </Box>
      )}
      <TableContainer component={Paper}>
        <Table>
          <TableHead><TableRow>
            <TableCell padding="checkbox">
              <Checkbox checked={allSelected} indeterminate={someSelected} onChange={toggleAll} inputProps={{ 'aria-label': 'Seleziona tutti i telefoni' }} />
            </TableCell>
            <TableCell>Dispositivo</TableCell><TableCell>Location</TableCell><TableCell>Tour visibili</TableCell>
            <TableCell>Ultimo accesso</TableCell><TableCell align="right">Azioni</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {devices.map(device => (
              <TableRow key={device._id} hover selected={selectedInstallationIds.includes(device.installationId)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedInstallationIds.includes(device.installationId)}
                    onChange={() => toggleDevice(device.installationId)}
                    inputProps={{ 'aria-label': `Seleziona ${device.deviceName}` }}
                  />
                </TableCell>
                <TableCell>
                  <Typography>{device.deviceName}</Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{device.installationId}</Typography>
                </TableCell>
                <TableCell>{device.location || 'Non assegnata'}</TableCell>
                <TableCell>
                  {device.tourAccessMode === 'all'
                    ? <Chip label="Tutti" color="success" size="small" />
                    : <Chip label={`${device.allowedTourIds?.length || 0} selezionati`} color="primary" size="small" />}
                </TableCell>
                <TableCell>{new Date(device.lastSeen).toLocaleString('it-IT')}</TableCell>
                <TableCell align="right">
                  <Box display="flex" gap={1} justifyContent="flex-end">
                    <Button onClick={() => edit(device)}>Configura</Button>
                    <Button color="error" onClick={() => remove(device)}>Elimina</Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {devices.length === 0 && <TableRow><TableCell colSpan={6} align="center">Nessun telefono registrato.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Configura {selected?.deviceName}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: '16px !important' }}>
          <TextField
            label="Nome dispositivo"
            value={deviceName}
            onChange={event => setDeviceName(event.target.value)}
            required
            fullWidth
          />
          <TextField label="Location" value={location} onChange={event => setLocation(event.target.value)} fullWidth />
          <FormControl fullWidth>
            <InputLabel>Accesso ai tour</InputLabel>
            <Select value={accessMode} label="Accesso ai tour" onChange={event => setAccessMode(event.target.value as 'all' | 'selected')}>
              <MenuItem value="all">Tutti i tour</MenuItem>
              <MenuItem value="selected">Solo i tour selezionati</MenuItem>
            </Select>
          </FormControl>
          {accessMode === 'selected' && <FormControl fullWidth>
            <InputLabel>Tour consentiti</InputLabel>
            <Select
              multiple value={tourIds} label="Tour consentiti"
              onChange={event => setTourIds(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value)}
              renderValue={ids => ids.map(id => tours.find(tour => tour.id === id)?.name || id).join(', ')}
            >
              {tours.map(tour => <MenuItem key={tour.id} value={tour.id}>
                <Checkbox checked={tourIds.includes(tour.id)} /><ListItemText primary={tour.name} />
              </MenuItem>)}
            </Select>
          </FormControl>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Annulla</Button>
          <Button variant="contained" onClick={save} disabled={!deviceName.trim()}>Salva</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Configura {selectedInstallationIds.length} telefoni</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: '16px !important' }}>
          <TextField
            label="Prefisso nomi (opzionale)"
            value={bulkNamePrefix}
            onChange={event => setBulkNamePrefix(event.target.value)}
            helperText={bulkNamePrefix.trim()
              ? `I dispositivi diventeranno ${bulkNamePrefix.trim()} 1, ${bulkNamePrefix.trim()} 2, …`
              : 'Lascia vuoto per mantenere i nomi attuali'}
            fullWidth
          />
          <TextField
            label="Location"
            value={bulkLocation}
            onChange={event => setBulkLocation(event.target.value)}
            helperText="La stessa location verrà applicata a tutti i telefoni selezionati"
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Accesso ai tour</InputLabel>
            <Select value={bulkAccessMode} label="Accesso ai tour" onChange={event => setBulkAccessMode(event.target.value as 'all' | 'selected')}>
              <MenuItem value="all">Tutti i tour</MenuItem>
              <MenuItem value="selected">Solo i tour selezionati</MenuItem>
            </Select>
          </FormControl>
          {bulkAccessMode === 'selected' && <FormControl fullWidth>
            <InputLabel>Tour consentiti</InputLabel>
            <Select
              multiple value={bulkTourIds} label="Tour consentiti"
              onChange={event => setBulkTourIds(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value)}
              renderValue={ids => ids.map(id => tours.find(tour => tour.id === id)?.name || id).join(', ')}
            >
              {tours.map(tour => <MenuItem key={tour.id} value={tour.id}>
                <Checkbox checked={bulkTourIds.includes(tour.id)} /><ListItemText primary={tour.name} />
              </MenuItem>)}
            </Select>
          </FormControl>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={saveBulk}>Applica a tutti</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppInstallations;
